// ./src/components/generic/DashboardWidget/DashboardWidget.tsx
//
// The card every dashboard widget sits in — icon, title, an optional action, and a header spinner.
//
// ⚠ EXTRACTED FROM `eventDashboardWidgets.tsx` (ROADMAP A2b #50) rather than copied. Home needed the
// same shell, and a second copy of it is how two dashboards in one product start drifting apart a
// padding at a time. The event dashboard re-exports this one, so every existing import still works.

import React from "react";
import { Box, Card, Group, Text, ThemeIcon, Loader } from "@mantine/core";

import { AppIcon } from "../AppIcon";

export interface DashboardWidgetProps {
  title: string;
  iconName: string;
  iconColor?: string;
  action?: React.ReactNode;
  /**
   * A fetch is in flight for this widget — shows a spinner in the header.
   *
   * ⚠ This is the ONLY signal a REFRESH gives. A widget deliberately keeps its previous numbers on
   * screen while refetching (it renders its skeleton on `!data`, not on `state === 'loading'`), so
   * without this the dashboard looks completely inert after Refresh until the values happen to
   * change — and if they haven't changed, the host cannot tell the refresh ran at all.
   */
  loading?: boolean;
  /**
   * ROADMAP #183 — a quieter second link at the card's FOOT ("Manage ticket types →"): the header's `action` opens the
   * card's report, this one the page where its records are edited. Never a second link in the header — a title, a
   * spinner and two links do not fit a phone's card.
   */
  footer?: React.ReactNode;
  children: React.ReactNode;
}

export const DashboardWidget: React.FC<DashboardWidgetProps> = ({
  title,
  iconName,
  iconColor = "brand",
  action,
  loading,
  footer,
  children,
}) => (
  // A column, so the footer sits at the bottom of a card stretched to its row-mate's height.
  <Card withBorder radius="md" padding="md" h="100%" style={{ display: "flex", flexDirection: "column" }}>
    <Group justify="space-between" wrap="nowrap" mb="sm">
      <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
        <ThemeIcon variant="light" color={iconColor} size={30} radius="md" style={{ flexShrink: 0 }}>
          <AppIcon iconName={iconName} size={16} />
        </ThemeIcon>
        <Text fw={600} size="md" truncate>
          {title}
        </Text>
        {/* Beside the TITLE, not the action, so it never displaces "View all". */}
        {loading && <Loader size="xs" style={{ flexShrink: 0 }} />}
      </Group>
      {action}
    </Group>
    <Box style={{ flex: 1 }}>{children}</Box>
    {footer && (
      <Group justify="flex-end" mt="sm">
        {footer}
      </Group>
    )}
  </Card>
);
